import { useEffect, useId, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  api,
  type Cliente,
  type EmisorEnRiesgo,
  type EstadoCredencial,
  type EstadoDelSelloDigital,
  type EstadoDeSincronizacion69B,
  type RegimenFiscal,
  type ReglaDeContabilizacion,
  type ResumenPeriodo,
  type RiesgoDeContribuyente,
  type Usuario,
  type UsuarioAsignado,
} from '../servicios/api'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { telon } from '../ganchos/telon'
import { fechaContable } from '../fechas'

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/**
 * Un INSTANTE (campo con sufijo `Utc`) sí se convierte a la hora local: es lo correcto.
 * Para una fecha contable (`DateOnly`) usa `fechaContable` — ver `src/fechas.ts`.
 */
const fechaDeInstante = (iso: string) => new Date(iso).toLocaleDateString('es-MX')

/**
 * Un campo de esta tarjeta ocupa su columna entera: `.datos` es una rejilla de etiqueta y
 * valor, y un `input` sin ancho se queda del tamaño que le dé el navegador.
 */
const ANCHO_DE_CAMPO = { width: '100%' }

/**
 * Datos fiscales del cliente (E1). La misma tarjeta los lee y los corrige: razón social,
 * régimen, código postal, correo e inicio de operaciones se editan en sitio (RF-201).
 * El RFC no: se validó al dar de alta y cambiarlo sería otro contribuyente, así que se queda
 * fijo también en modo edición.
 * La CSF (E2) va de pie de esta tarjeta y no como sección aparte: es el respaldo de estos datos.
 */
function TarjetaDeDatosFiscales({ cliente, tieneCsf, clienteId, onCargarCsf, onActualizado }: {
  cliente: Cliente; tieneCsf: boolean | null; clienteId: string
  onCargarCsf: () => void; onActualizado: (cliente: Cliente) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [editando, setEditando] = useState(false)

  async function descargar() {
    setError(null)
    try {
      await api.descargarConstanciaFiscal(clienteId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo descargar la constancia.')
    }
  }

  return (
    <div className="tarjeta">
      <div className="tarjeta-cabecera">
        <h2>Datos fiscales</h2>
        {/* En una lista de botones, un «Editar» suelto no dice qué edita: el nombre accesible
            lo deletrea y el visible sigue siendo el del prototipo. */}
        {!editando && (
          <button className="enlace" aria-label="Editar los datos fiscales"
            onClick={() => setEditando(true)}>
            Editar
          </button>
        )}
      </div>

      {editando ? (
        <FormularioDeDatosFiscales
          cliente={cliente}
          onCancelar={() => setEditando(false)}
          onGuardado={(actualizado) => {
            setEditando(false)
            onActualizado(actualizado)
          }}
        />
      ) : (
        <dl className="datos">
          <dt>Razón social</dt>
          <dd>{cliente.razonSocial}</dd>
          <dt>RFC</dt>
          <dd className="mono">{cliente.rfc}</dd>
          <dt>Régimen</dt>
          <dd>{cliente.regimenFiscal} · {cliente.regimenDescripcion}</dd>
          <dt>Código postal</dt>
          <dd className="mono">{cliente.codigoPostalFiscal}</dd>
          {/* El correo no siempre se captura en el alta: el renglón en blanco no distinguiría
              «no lo tenemos» de «se perdió al pintarlo». */}
          <dt>Correo</dt>
          <dd>{cliente.correoContacto || <span className="secundaria">Sin correo de contacto</span>}</dd>
          <dt>Inicio de operaciones</dt>
          <dd>{fechaContable(cliente.fechaInicioOperaciones)}</dd>
          {/* RF-1209: de aquí sale la fecha límite de sus pagos provisionales en el tablero. */}
          <dt>Pagos provisionales</dt>
          <dd>
            {cliente.excluidoDelPlazoEscalonado
              ? 'Vencen el día 17: excluido del plazo escalonado (art. 5.1)'
              : 'Día 17 más los días hábiles del sexto dígito del RFC (art. 5.1)'}
          </dd>
        </dl>
      )}

      <p className="nota">
        El RFC se validó contra su estructura oficial al dar de alta al cliente y no se puede
        cambiar desde aquí (RF-201).
      </p>

      <div className="tarjeta-cabecera" style={{ marginTop: 'var(--space-4)' }}>
        <h2>Constancia de Situación Fiscal</h2>
        <button className="enlace" onClick={onCargarCsf}>{tieneCsf ? 'Reemplazar' : 'Cargar'}</button>
      </div>
      {tieneCsf ? (
        <p className="menuda">
          Constancia en el expediente ·{' '}
          <button className="enlace" onClick={() => void descargar()}>Descargar</button>
        </p>
      ) : (
        <p className="menuda">Este cliente aún no tiene una Constancia de Situación Fiscal en su expediente.</p>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  )
}

/**
 * Corrección de los datos fiscales (E1, RF-201). Va dentro de la tarjeta y no en un modal: es
 * una corrección sobre lo que se está leyendo, no una alta.
 *
 * El API espera el juego completo de datos —es un PUT—, así que cada campo arranca con lo que
 * hay guardado y no en blanco: lo que no se reenvíe se pierde.
 */
function FormularioDeDatosFiscales({ cliente, onCancelar, onGuardado }: {
  cliente: Cliente; onCancelar: () => void; onGuardado: (cliente: Cliente) => void
}) {
  const id = useId()
  const [razonSocial, setRazonSocial] = useState(cliente.razonSocial)
  const [regimenFiscal, setRegimenFiscal] = useState(cliente.regimenFiscal)
  const [codigoPostalFiscal, setCodigoPostalFiscal] = useState(cliente.codigoPostalFiscal)
  const [fechaInicioOperaciones, setFechaInicioOperaciones] =
    useState(cliente.fechaInicioOperaciones.slice(0, 10))
  const [correoContacto, setCorreoContacto] = useState(cliente.correoContacto ?? '')
  const [excluido, setExcluido] = useState(cliente.excluidoDelPlazoEscalonado ?? false)
  const [regimenes, setRegimenes] = useState<RegimenFiscal[]>([])
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  // El catálogo se pide para el tipo de persona que el cliente YA tiene, porque ese no se
  // edita: ofrecerle a una persona moral un régimen de física sería ofrecerle un error (RN-11).
  useEffect(() => {
    api.regimenesFiscales(cliente.tipoPersona)
      .then(setRegimenes)
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo de regímenes.'))
  }, [cliente.tipoPersona])

  // Un régimen que ya no aparece en el catálogo vigente se conserva a la vista y se avisa; no
  // se cae al primero de la lista. Aquí se viene a corregir un código postal o un correo, y
  // cambiarle el régimen al cliente de paso, sin que nadie lo pidiera, sería el peor desenlace.
  const estaEnElCatalogo = regimenes.some((r) => r.clave === regimenFiscal)
  const regimenFueraDelCatalogo = !estaEnElCatalogo && regimenes.length > 0

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setGuardando(true)
    try {
      const actualizado = await api.editarCliente(cliente.id, {
        razonSocial,
        regimenFiscal,
        codigoPostalFiscal,
        fechaInicioOperaciones,
        // Un campo en blanco quiere decir «este cliente no tiene correo», y eso se manda como
        // nulo: la cadena vacía se guardaría tal cual, como un correo que es una cadena vacía.
        correoContacto: correoContacto.trim() || null,
        excluidoDelPlazoEscalonado: excluido,
      })
      onGuardado(actualizado)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron guardar los datos fiscales.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <form onSubmit={guardar}>
      <dl className="datos">
        <dt><label htmlFor={`${id}-razon`}>Razón social</label></dt>
        <dd>
          {/* El formulario solo se monta al pulsar «Editar», así que el foco va al primer
              campo: quien navega con teclado no tiene que volver a buscarlo. */}
          <input id={`${id}-razon`} style={ANCHO_DE_CAMPO} required maxLength={300} autoFocus
            value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} />
        </dd>
        {/* El RFC se queda de lectura en medio del formulario: es el único dato de la tarjeta
            que no se toca, y sacarlo de aquí lo haría parecer un olvido. */}
        <dt>RFC</dt>
        <dd className="mono">{cliente.rfc}</dd>
        <dt><label htmlFor={`${id}-regimen`}>Régimen</label></dt>
        <dd>
          <select id={`${id}-regimen`} style={ANCHO_DE_CAMPO} required
            value={regimenFiscal} onChange={(e) => setRegimenFiscal(e.target.value)}>
            {!estaEnElCatalogo && (
              <option value={regimenFiscal}>{regimenFiscal} · {cliente.regimenDescripcion}</option>
            )}
            {regimenes.map((r) => (
              <option key={r.clave} value={r.clave}>{r.clave} · {r.descripcion}</option>
            ))}
          </select>
        </dd>
        <dt><label htmlFor={`${id}-cp`}>Código postal</label></dt>
        <dd>
          <input id={`${id}-cp`} className="mono" style={ANCHO_DE_CAMPO} required
            pattern="\d{5}" maxLength={5} value={codigoPostalFiscal}
            onChange={(e) => setCodigoPostalFiscal(e.target.value)} />
        </dd>
        <dt><label htmlFor={`${id}-correo`}>Correo</label></dt>
        <dd>
          <input id={`${id}-correo`} type="email" style={ANCHO_DE_CAMPO} value={correoContacto}
            onChange={(e) => setCorreoContacto(e.target.value)} />
        </dd>
        <dt><label htmlFor={`${id}-inicio`}>Inicio de operaciones</label></dt>
        <dd>
          <input id={`${id}-inicio`} type="date" style={ANCHO_DE_CAMPO} required
            value={fechaInicioOperaciones} onChange={(e) => setFechaInicioOperaciones(e.target.value)} />
        </dd>
        <dt>Pagos provisionales</dt>
        <dd>
          {/* RF-1209: ContaQuick no puede saber si el cliente cae en un supuesto del art. 5.1; si le
              diera los días del sexto dígito sin corresponderle, lo haría declarar tarde. */}
          <label>
            <input type="checkbox" checked={excluido} onChange={(e) => setExcluido(e.target.checked)} />{' '}
            Excluido del plazo escalonado (art. 5.1)
          </label>
          <span className="menuda" style={{ display: 'block' }}>
            Márcalo si el cliente dictamina sus estados financieros o es del 32-H, gran contribuyente, gobierno, partido
            político, integrada o integradora: sus pagos vencen el día 17.
          </span>
        </dd>
      </dl>

      {regimenFueraDelCatalogo && (
        <p className="aviso" role="status">
          El régimen {regimenFiscal} ya no está en el catálogo vigente del SAT para este tipo de
          persona. Se conserva tal como está guardado; para poder guardar hay que elegir uno
          de los vigentes.
        </p>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <div className="acciones">
        <button type="button" onClick={onCancelar}>Cancelar</button>
        <button type="submit" className="primario" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  )
}

/**
 * Expediente fiscal del cliente con la custodia de su e.firma.
 * SRS: RF-203, RF-204. Cédula: A-03, A-04.
 */
export function ExpedienteCliente() {
  const { id = '' } = useParams()
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [credencial, setCredencial] = useState<EstadoCredencial | null>(null)
  const [sello, setSello] = useState<EstadoDelSelloDigital | null>(null)
  const [mostrarCargaSello, setMostrarCargaSello] = useState(false)
  const [riesgo69b, setRiesgo69b] = useState<RiesgoDeContribuyente | null>(null)
  const [tieneCsf, setTieneCsf] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mostrarCarga, setMostrarCarga] = useState(false)
  const [mostrarCargaCsf, setMostrarCargaCsf] = useState(false)
  const [confirmandoArchivo, setConfirmandoArchivo] = useState(false)
  // Tarjetas que la pantalla solo enlazaba (E4, E6, E8): los datos ya estaban en la API.
  const [periodos, setPeriodos] = useState<ResumenPeriodo[]>([])
  const [reglas, setReglas] = useState<ReglaDeContabilizacion[]>([])
  const [emisoresEnRiesgo, setEmisoresEnRiesgo] = useState<EmisorEnRiesgo[]>([])
  const [sincronizacion69b, setSincronizacion69b] = useState<EstadoDeSincronizacion69B | null>(null)

  const recargar = async () => {
    try {
      setCliente(await api.obtenerCliente(id))
      setCredencial(await api.credencialDeCliente(id))
      setSello(await api.selloDigitalDeCliente(id))
      setRiesgo69b(await api.riesgo69bDelCliente(id))
      setTieneCsf(await api.estadoConstanciaFiscal(id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el expediente.')
    }
  }

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Estas cuatro no bloquean el expediente: si alguna falla, su tarjeta lo dice y las demás
  // siguen. El año del calendario basta para los últimos periodos, que es lo que se muestra.
  useEffect(() => {
    if (!id) return
    api.listarPeriodos(id, new Date().getFullYear()).then(setPeriodos).catch(() => setPeriodos([]))
    api.listarReglas(id).then(setReglas).catch(() => setReglas([]))
    api.emisoresEnRiesgo(id).then(setEmisoresEnRiesgo).catch(() => setEmisoresEnRiesgo([]))
    api.estadoDeLista69b().then(setSincronizacion69b).catch(() => setSincronizacion69b(null))
  }, [id])

  // A-05 / I-05: archivar es lógico — la contabilidad, los CFDI y los acuses siguen consultables.
  // Vive aquí y no en la lista: es la acción más destructiva de la cartera y merece el expediente
  // delante, no una fila entre veinte.
  async function archivar() {
    setError(null)
    try {
      await api.archivarCliente(id)
      setConfirmandoArchivo(false)
      await recargar()
    } catch (e) {
      setConfirmandoArchivo(false)
      setError(e instanceof Error ? e.message : 'No se pudo archivar el cliente.')
    }
  }

  if (error) return <p className="error" role="alert">{error}</p>
  if (!cliente) return <p>Cargando…</p>

  return (
    <section>
      <p className="sutil">
        <Link to="/clientes">← Clientes</Link>
      </p>

      <header className="encabezado">
        <h1>{cliente.razonSocial}</h1>
        <span className="encabezado-sub">
          <span className="insignia neutra">
            {cliente.tipoPersona === 'Moral' ? 'PM' : 'PF'} · {cliente.regimenFiscal}
          </span>{' '}
          <span className={`insignia ${cliente.estado === 'Archivado' ? 'neutra' : 'vigente'}`}>{cliente.estado}</span>
        </span>
        {cliente.estado !== 'Archivado' && (
          <span className="encabezado-acciones">
            <button onClick={() => setConfirmandoArchivo(true)}>Archivar cliente</button>
          </span>
        )}
      </header>
      <p className="sutil mono">
        {cliente.rfc} · {cliente.regimenFiscal} {cliente.regimenDescripcion}
      </p>

      {riesgo69b?.esRiesgoActivo && (
        <p className="aviso-critico" role="alert" style={{ display: 'block' }}>
          <strong>Este RFC aparece en el listado 69-B del SAT</strong> ({riesgo69b.situacion}
          {riesgo69b.fechaPublicacion ? ` · publicado ${fechaContable(riesgo69b.fechaPublicacion)}` : ''}
          ). Revisa el caso antes de emitir o deducir operaciones con este contribuyente (RF-206).
        </p>
      )}

      {/* Ocho enlaces en un párrafo separados por puntos no son navegación: son una frase.
          En barra de pestañas se ve de dónde a dónde se puede ir (E11). */}
      <div className="pestanas" style={{ marginTop: 'var(--space-4)' }}>
        {[
          ['catalogo', 'Catálogo de cuentas'],
          ['polizas', 'Pólizas'],
          ['plantillas-poliza', 'Plantillas recurrentes'],
          ['cfdi', 'CFDI'],
          ['contabilizacion', 'Contabilización'],
          ['reportes', 'Reportes'],
          ['control-envios', 'Control de envíos'],
          ['balanza-ajustada', 'Balanza ajustada'],
          ['iva', 'Papel de IVA'],
          ['isr', 'Papel de ISR'],
          ['declaraciones', 'Declaraciones'],
          ['conciliacion-cfdi', 'Conciliación CFDI'],
          ['conciliacion-bancaria', 'Conciliación bancaria'],
          ['folios', 'Series y folios'],
        ].map(([ruta, etiqueta]) => (
          <Link key={ruta} className="pestana" to={`/clientes/${cliente.id}/${ruta}`}>{etiqueta}</Link>
        ))}
      </div>

      {confirmandoArchivo && (
        <ModalDeArchivo cliente={cliente} onCerrar={() => setConfirmandoArchivo(false)} onConfirmar={() => void archivar()} />
      )}

      <div className="rejilla-expediente">
        <div>
          {/* `onActualizado` recibe lo que devolvió el PUT, ya normalizado por el servidor:
              la pantalla repinta con eso y no con lo que se tecleó, sin un GET de más. */}
          <TarjetaDeDatosFiscales
            cliente={cliente}
            tieneCsf={tieneCsf}
            clienteId={id}
            onCargarCsf={() => setMostrarCargaCsf(true)}
            onActualizado={setCliente}
          />

          <div className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
            <div className="tarjeta-cabecera">
              <h2>Periodos contables</h2>
              <Link className="enlace" to={`/clientes/${cliente.id}/polizas`}>Ver todos</Link>
            </div>
            {periodos.length === 0 ? (
              <p className="vacio">Todavía no se abre ningún periodo de este ejercicio.</p>
            ) : (
              <ul className="lista-compacta">
                {[...periodos].sort((a, b) => b.mes - a.mes).slice(0, 3).map((p) => (
                  <li key={`${p.anio}-${p.mes}`}>
                    <span>{MESES[p.mes - 1]} {p.anio}</span>
                    <span>
                      <span className="secundaria">{p.polizas} póliza(s)</span>{' '}
                      <span className={`insignia ${p.estado === 'Cerrado' ? 'cerrada' : 'suave'}`}>{p.estado}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="nota">
              Un periodo cerrado ya no admite pólizas nuevas ni cambios: para corregirlo hay que
              reabrirlo, y esa reapertura queda en la bitácora (RN-06).
            </p>
          </div>

          <div className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
            <div className="tarjeta-cabecera">
              <h2>Reglas de contabilización</h2>
              <Link className="enlace" to={`/clientes/${cliente.id}/contabilizacion`}>Ver todas</Link>
            </div>
            {reglas.filter((r) => r.estado === 'Activa').length === 0 ? (
              <p className="vacio">Sin reglas activas: cada CFDI se contabiliza a mano.</p>
            ) : (
              <ul className="lista-compacta">
                {reglas.filter((r) => r.estado === 'Activa').slice(0, 3).map((r) => (
                  <li key={r.id}>
                    <span className="mono">{r.rfcEmisor}</span>
                    <span className="secundaria">{r.descripcion ?? r.cuentaGastoTexto}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <div className="tarjeta">
            <div className="tarjeta-cabecera">
              <h2>e.firma del cliente</h2>
              {credencial && <button className="enlace" onClick={() => setMostrarCarga(true)}>Reemplazar</button>}
            </div>
            {credencial ? (
              <TarjetaDeCredencial credencial={credencial} onRevocada={recargar} />
            ) : (
              <>
                <p>Este cliente aún no tiene e.firma resguardada.</p>
                <p className="nota">
                  Se necesita para descargar sus CFDI del SAT. Requiere el consentimiento del contribuyente.
                </p>
                <div className="acciones">
                  <button className="primario" onClick={() => setMostrarCarga(true)}>Cargar e.firma</button>
                </div>
              </>
            )}
          </div>

          {/* RF-1001: el CSD tiene tarjeta propia y no comparte la de la e.firma a propósito.
              Son materiales parecidos con destinos opuestos —el CSD se entrega al proveedor de
              timbrado, la e.firma nunca sale— y juntarlos invitaría justo a la confusión que
              ADR-013 vuelve cara. */}
          <div className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
            <div className="tarjeta-cabecera">
              <h2>Sello digital (CSD)</h2>
              {sello?.credencial && (
                <button className="enlace" onClick={() => setMostrarCargaSello(true)}>Reemplazar</button>
              )}
            </div>
            {sello?.credencial ? (
              <TarjetaDelSello sello={sello} onCambio={recargar} />
            ) : (
              <>
                <p>Este cliente aún no tiene CSD resguardado.</p>
                <p className="nota">
                  Se necesita para emitir y timbrar sus comprobantes. A diferencia de la e.firma, el
                  CSD se entrega al proveedor de timbrado, que es quien sella: eso requiere el
                  consentimiento expreso del contribuyente.
                </p>
                <div className="acciones">
                  <button className="primario" onClick={() => setMostrarCargaSello(true)}>Cargar CSD</button>
                </div>
              </>
            )}
          </div>

          {/* E6: el estado bueno —«no listado»— también es información. Si la tarjeta solo
              apareciera con riesgo, el silencio no distinguiría «limpio» de «sin revisar». */}
          <div className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
            <div className="tarjeta-cabecera">
              <h2>Verificación 69-B (EFOS)</h2>
              <Link className="enlace" to="/clientes">Sincronizar</Link>
            </div>
            <ul className="lista-compacta">
              <li>
                <span>Este cliente</span>
                {riesgo69b?.esRiesgoActivo
                  ? <span className="insignia vencido">{riesgo69b.situacion}</span>
                  : <span className="insignia vigente">No listado</span>}
              </li>
              <li>
                <span>Sus proveedores</span>
                {emisoresEnRiesgo.length === 0
                  ? <span className="insignia vigente">Limpios</span>
                  : <span className="insignia vencido">{emisoresEnRiesgo.length} en el listado</span>}
              </li>
            </ul>
            <p className="nota">
              {sincronizacion69b
                ? `Listado sincronizado el ${new Date(sincronizacion69b.ultimaSincronizacionUtc).toLocaleDateString('es-MX')} (${sincronizacion69b.origen}).`
                : 'El listado 69-B nunca se ha sincronizado: hasta entonces nadie se marca en riesgo.'}
            </p>
          </div>

          <TarjetaDeUsuariosAsignados clienteId={id} razonSocial={cliente.razonSocial} />
        </div>
      </div>

      {mostrarCargaSello && (
        <FormularioDeSello
          clienteId={id}
          rfcEsperado={cliente.rfc}
          onCerrar={() => setMostrarCargaSello(false)}
          onCargada={() => { setMostrarCargaSello(false); void recargar() }}
        />
      )}

      {mostrarCarga && (
        <FormularioDeCarga
          clienteId={id}
          rfcEsperado={cliente.rfc}
          onCerrar={() => setMostrarCarga(false)}
          onCargada={() => {
            setMostrarCarga(false)
            void recargar()
          }}
        />
      )}

      {mostrarCargaCsf && (
        <FormularioDeCargaCsf
          clienteId={id}
          onCerrar={() => setMostrarCargaCsf(false)}
          onCargada={() => {
            setMostrarCargaCsf(false)
            void recargar()
          }}
        />
      )}
    </section>
  )
}

/**
 * Confirmación de archivado (RF-205). Un `window.confirm` no dice qué se conserva ni qué se
 * pierde, y esta es la acción más destructiva de la cartera: el modal lo deletrea.
 */
function ModalDeArchivo({ cliente, onCerrar, onConfirmar }: {
  cliente: Cliente; onCerrar: () => void; onConfirmar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo}
        className="panel" onClick={(e) => e.stopPropagation()}>
        <h2 id={idTitulo}>Archivar a {cliente.razonSocial}</h2>
        <p>
          <span className="mono">{cliente.rfc}</span> dejará de aparecer en la cartera activa y no
          generará obligaciones nuevas.
        </p>
        <p className="nota">
          Su contabilidad, sus CFDI y sus acuses siguen consultables: archivar no borra nada y se
          puede revertir desde el filtro «Estatus: archivados» de la lista de clientes.
        </p>
        <div className="acciones">
          <button onClick={onCerrar}>Cancelar</button>
          <button className="primario" onClick={onConfirmar}>Archivar cliente</button>
        </div>
      </div>
    </div>
  )
}

function TarjetaDeCredencial({ credencial, onRevocada }: {
  credencial: EstadoCredencial
  onRevocada: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [revocando, setRevocando] = useState(false)
  const dias = credencial.diasParaVencer

  // Semáforo alineado con los umbrales del SRS: 90 / 30 / 7 días (RF-204).
  const semaforo = dias < 0 ? 'vencido' : dias <= 30 ? 'urgente' : dias <= 90 ? 'aviso' : 'vigente'
  // El chip dice la fecha Y los días: «vence en 39 días» sin fecha obliga a calcularla, y la
  // fecha sola obliga a contar. El contador necesita las dos para decidir si actúa hoy.
  const leyenda =
    dias < 0
      ? `Venció el ${fechaDeInstante(credencial.vigenciaFinUtc)} · hace ${Math.abs(dias)} días`
      : `Vence ${fechaDeInstante(credencial.vigenciaFinUtc)} · ${dias} días`

  async function revocar(motivo: string) {
    try {
      await api.revocarCredencial(credencial.clienteId, credencial.credencialId, motivo)
      setRevocando(false)
      onRevocada()
    } catch (e) {
      setRevocando(false)
      setError(e instanceof Error ? e.message : 'No se pudo revocar.')
    }
  }

  return (
    <>
      <p style={{ margin: '0 0 var(--space-3)' }}>
        <span className={`insignia ${semaforo}`}>{leyenda}</span>
      </p>

      {/* Las dos piezas del material existen y se ven; descargarlas no es posible a propósito. */}
      <div className="archivos-efirma">
        <span className="caja-archivo">.cer certificado</span>
        <span className="caja-archivo">.key llave privada</span>
      </div>

      <dl className="datos">
        <dt>Vigencia</dt>
        <dd>
          {fechaDeInstante(credencial.vigenciaInicioUtc)} – {fechaDeInstante(credencial.vigenciaFinUtc)}
        </dd>
        <dt>Consentimiento</dt>
        <dd>
          {credencial.consentimientoProposito} · {fechaDeInstante(credencial.consentimientoFechaUtc)}
        </dd>
      </dl>

      <p className="nota">
        El certificado y la llave están cifrados fuera de esta aplicación. Nadie —tú incluido— puede
        descargarlos: solo los usan los procesos autorizados, y cada uso queda en la bitácora.
        Se avisa del vencimiento a los 90, 30 y 7 días.
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="acciones">
        <button onClick={() => setRevocando(true)}>Revocar</button>
      </div>

      {revocando && (
        <ModalDeRevocacion onCerrar={() => setRevocando(false)} onConfirmar={(motivo) => void revocar(motivo)} />
      )}
    </>
  )
}

/**
 * Motivo de la revocación (E12). Antes era un `window.prompt`: sin foco atrapado, sin poder
 * cancelar con Escape y sin decir que el motivo queda en la bitácora.
 */
function ModalDeRevocacion({
  onCerrar,
  onConfirmar,
  titulo = 'Revocar la e.firma',
  descripcion = 'El material cifrado se borra del resguardo y el cliente deja de poder descargar CFDI hasta que se cargue una e.firma nueva.',
  etiquetaBoton = 'Revocar e.firma',
}: {
  onCerrar: () => void
  onConfirmar: (motivo: string) => void
  titulo?: string
  descripcion?: string
  etiquetaBoton?: string
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [motivo, setMotivo] = useState('')

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onConfirmar(motivo.trim()) }}
      >
        <h2 id={idTitulo}>{titulo}</h2>
        <p>{descripcion}</p>
        <label>
          ¿Por qué la revocas?
          <input required maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        <p className="nota">El motivo y tu nombre quedan en la bitácora (RF-203).</p>
        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="primario" disabled={!motivo.trim()}>{etiquetaBoton}</button>
        </div>
      </form>
    </div>
  )
}

function FormularioDeCarga({
  clienteId,
  rfcEsperado,
  onCerrar,
  onCargada,
}: {
  clienteId: string
  rfcEsperado: string
  onCerrar: () => void
  onCargada: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [error, setError] = useState<string | null>(null)
  const [advertencia, setAdvertencia] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setAdvertencia(null)

    const datos = new FormData(evento.currentTarget)

    if (!datos.get('consentimiento')) {
      setError('Debes confirmar que el cliente autorizó el uso de su e.firma.')
      return
    }

    setGuardando(true)
    try {
      const resultado = await api.cargarCredencial(clienteId, datos)
      if (resultado.advertencia) {
        setAdvertencia(resultado.advertencia)
        return // se queda abierto para que el contador lea la advertencia
      }
      onCargada()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la e.firma.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        <h2 id={idTitulo}>Cargar e.firma</h2>
        <p className="nota">
          Archivos del trámite de firma electrónica del SAT para <strong className="mono">{rfcEsperado}</strong>.
          El sistema verifica que el certificado corresponda a este RFC antes de resguardarlo.
        </p>

        <label>
          Certificado (.cer)
          <input type="file" name="cer" accept=".cer" required />
        </label>

        <label>
          Llave privada (.key)
          <input type="file" name="key" accept=".key" required />
        </label>

        <label>
          Contraseña de la llave privada
          <input type="password" name="contrasena" required autoComplete="off" />
        </label>

        <label>
          Uso autorizado por el cliente
          <select name="proposito" required defaultValue="Descarga masiva de CFDI del SAT">
            <option>Descarga masiva de CFDI del SAT</option>
            <option>Descarga de CFDI y envío de contabilidad electrónica</option>
            <option>Consulta de estatus de comprobantes</option>
          </select>
        </label>

        <label className="casilla">
          <input type="checkbox" name="consentimiento" value="si" />
          <span>
            Confirmo que el contribuyente autorizó expresamente el uso de su e.firma para el fin
            indicado, y que conservo constancia de esa autorización.
          </span>
        </label>

        {advertencia && <p className="aviso" role="status">{advertencia}</p>}
        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="primario" disabled={guardando}>
            {guardando ? 'Verificando…' : advertencia ? 'Cargar de todos modos' : 'Cargar'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * El CSD de aquí y el sello de allá, en la misma tarjeta y a la vista (RF-1001, RF-1002).
 * Que estén separados es el estado que hay que poder ver: un CSD perfectamente custodiado con el
 * proveedor sin enterarse no emite nada, y un CSD revocado que el proveedor todavía tiene es peor
 * todavía. Por eso el estado remoto tiene su propio chip y su propio botón de arreglarlo.
 */
function TarjetaDelSello({ sello, onCambio }: {
  sello: EstadoDelSelloDigital
  onCambio: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [revocando, setRevocando] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const credencial = sello.credencial!
  const dias = credencial.diasParaVencer

  const semaforo = dias < 0 ? 'vencido' : dias <= 30 ? 'urgente' : dias <= 90 ? 'aviso' : 'vigente'
  const leyenda =
    dias < 0
      ? `Venció el ${fechaDeInstante(credencial.vigenciaFinUtc)} · hace ${Math.abs(dias)} días`
      : `Vence ${fechaDeInstante(credencial.vigenciaFinUtc)} · ${dias} días`

  // El texto dice qué significa el estado para el contador, no cómo se llama por dentro.
  const remoto: Record<string, { clase: string; texto: string; explicacion: string }> = {
    Aprovisionado: {
      clase: 'vigente',
      texto: 'Listo para timbrar',
      explicacion: 'El proveedor de timbrado tiene este mismo sello y puede sellar comprobantes.',
    },
    SinSello: {
      clase: 'aviso',
      texto: 'El proveedor no tiene el sello',
      explicacion: 'El CSD está resguardado aquí pero el proveedor no lo tiene. No se puede timbrar.',
    },
    AprovisionamientoPendiente: {
      clase: 'urgente',
      texto: 'Falta entregarlo al proveedor',
      explicacion: 'El CSD quedó resguardado aquí, pero el proveedor no lo aceptó. No se puede timbrar hasta reintentar.',
    },
    RetiroPendiente: {
      clase: 'vencido',
      texto: 'El proveedor todavía lo tiene',
      explicacion: 'Se revocó aquí y el proveedor no lo soltó. Hay que retirarlo allá cuanto antes.',
    },
  }
  const estado = sello.estadoRemoto ? remoto[sello.estadoRemoto] : null

  async function sincronizar() {
    setError(null)
    setSincronizando(true)
    try {
      await api.sincronizarSelloDigital(credencial.clienteId)
      onCambio()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo sincronizar con el proveedor.')
    } finally {
      setSincronizando(false)
    }
  }

  async function revocar(motivo: string) {
    setRevocando(false)
    setError(null)
    try {
      await api.revocarSelloDigital(credencial.clienteId, credencial.credencialId, motivo)
      onCambio()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo revocar el CSD.')
    }
  }

  return (
    <>
      <p style={{ margin: '0 0 var(--space-3)' }}>
        <span className={`insignia ${semaforo}`}>{leyenda}</span>
        {estado && <span className={`insignia ${estado.clase}`} style={{ marginLeft: 'var(--space-2)' }}>{estado.texto}</span>}
      </p>

      {estado && <p className="nota">{estado.explicacion}</p>}

      <dl className="datos">
        <dt>Proveedor</dt>
        <dd>{sello.proveedor ?? '—'}</dd>
        <dt>Serie del sello</dt>
        <dd className="mono">{sello.numeroDeSerieDelSello ?? '—'}</dd>
        <dt>Consentimiento</dt>
        <dd>{credencial.consentimientoProposito} · {fechaDeInstante(credencial.consentimientoFechaUtc)}</dd>
      </dl>

      {sello.ultimoError && (
        <p className="aviso" role="status">Último rechazo del proveedor: {sello.ultimoError}</p>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <div className="acciones">
        {!sello.puedeEmitir && (
          <button onClick={() => void sincronizar()} disabled={sincronizando}>
            {sincronizando ? 'Sincronizando…' : 'Sincronizar con el proveedor'}
          </button>
        )}
        <button onClick={() => setRevocando(true)}>Revocar</button>
      </div>

      {revocando && (
        <ModalDeRevocacion
          titulo="Revocar el CSD"
          descripcion="Primero se retira del proveedor de timbrado y después se borra del resguardo. El cliente deja de poder emitir hasta que se cargue un CSD nuevo."
          etiquetaBoton="Revocar CSD"
          onCerrar={() => setRevocando(false)}
          onConfirmar={(motivo) => void revocar(motivo)}
        />
      )}
    </>
  )
}

function FormularioDeSello({
  clienteId,
  rfcEsperado,
  onCerrar,
  onCargada,
}: {
  clienteId: string
  rfcEsperado: string
  onCerrar: () => void
  onCargada: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [error, setError] = useState<string | null>(null)
  const [advertencia, setAdvertencia] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setAdvertencia(null)

    const datos = new FormData(evento.currentTarget)

    if (!datos.get('consentimiento')) {
      setError('Debes confirmar que el cliente autorizó entregar su CSD al proveedor de timbrado.')
      return
    }

    setGuardando(true)
    try {
      const resultado = await api.cargarSelloDigital(clienteId, datos)
      if (resultado.advertencia) {
        // El CSD ya quedó resguardado; lo que falta es del lado del proveedor. Se deja el
        // aviso a la vista en lugar de cerrar como si todo hubiera salido bien.
        setAdvertencia(resultado.advertencia)
        return
      }
      onCargada()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el CSD.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        <h2 id={idTitulo}>Cargar CSD</h2>
        <p className="nota">
          Archivos del Certificado de Sello Digital del SAT para <strong className="mono">{rfcEsperado}</strong>.
          Si subes la e.firma por error, el sistema la rechaza: la e.firma nunca se entrega al
          proveedor de timbrado.
        </p>

        <label>
          Certificado (.cer)
          <input type="file" name="cer" accept=".cer" required />
        </label>

        <label>
          Llave privada (.key)
          <input type="file" name="key" accept=".key" required />
        </label>

        <label>
          Contraseña de la llave privada
          <input type="password" name="contrasena" required autoComplete="off" />
        </label>

        <input type="hidden" name="proposito" value="Sellar y timbrar los comprobantes del contribuyente" />

        <label className="casilla">
          <input type="checkbox" name="consentimiento" value="si" />
          <span>
            Confirmo que el contribuyente autorizó expresamente usar su CSD para emitir sus
            comprobantes y entregarlo al proveedor de timbrado, y que conservo constancia de esa
            autorización.
          </span>
        </label>

        {advertencia && <p className="aviso" role="status">{advertencia}</p>}
        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="primario" disabled={guardando}>
            {guardando ? 'Verificando…' : 'Cargar'}
          </button>
        </div>
      </form>
    </div>
  )
}

function FormularioDeCargaCsf({
  clienteId,
  onCerrar,
  onCargada,
}: {
  clienteId: string
  onCerrar: () => void
  onCargada: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)

    if (!archivo) {
      setError('Elige el archivo PDF de la Constancia de Situación Fiscal.')
      return
    }

    setGuardando(true)
    try {
      await api.cargarConstanciaFiscal(clienteId, archivo)
      onCargada()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la constancia.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        <h2 id={idTitulo}>Cargar Constancia de Situación Fiscal</h2>
        <p className="nota">
          Sube el PDF de la CSF descargada del portal del SAT. Reemplaza a la anterior si ya había una.
        </p>

        <label>
          Constancia de Situación Fiscal (PDF)
          <input
            type="file"
            accept="application/pdf,.pdf"
            required
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="primario" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Quién atiende a este cliente (E7, RF-104). La cartera solo se podía ver desde Usuarios, con
 * la relación al revés: «qué clientes lleva Fulano». Desde el expediente la pregunta natural es
 * la contraria, y hasta ahora no había forma de responderla sin recorrer usuario por usuario.
 *
 * Asignar y revocar reescriben la cartera completa del colaborador, porque el único endpoint
 * que existe es un PUT de la lista entera: se lee, se agrega o se quita este cliente y se
 * vuelve a mandar. Si dos personas la editan a la vez, gana la última.
 */
export function TarjetaDeUsuariosAsignados({ clienteId, razonSocial }: { clienteId: string; razonSocial: string }) {
  const [asignados, setAsignados] = useState<UsuarioAsignado[]>()
  const [plantilla, setPlantilla] = useState<Usuario[]>([])
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState('')

  async function recargar() {
    try {
      setAsignados(await api.usuariosAsignados(clienteId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo consultar quién atiende a este cliente.')
    }
  }

  useEffect(() => {
    void recargar()
    // La plantilla es para el desplegable de «Asignar»: sin ella la tarjeta sigue sirviendo
    // para ver quién atiende, así que su fallo no se anuncia.
    api.listarUsuarios().then(setPlantilla).catch(() => setPlantilla([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  async function cambiarCartera(usuarioId: string, incluir: boolean) {
    setOcupado(usuarioId)
    setError('')
    try {
      const cartera = await api.clientesAsignados(usuarioId)
      const nueva = incluir
        ? [...new Set([...cartera, clienteId])]
        : cartera.filter((c) => c !== clienteId)
      await api.asignarClientes(usuarioId, nueva)
      await recargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar la cartera.')
    } finally {
      setOcupado('')
    }
  }

  const disponibles = plantilla.filter(
    (u) => u.estado === 'Activo' && !(asignados ?? []).some((a) => a.id === u.id),
  )

  return (
    <div className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
      <div className="tarjeta-cabecera">
        <h2>Usuarios asignados</h2>
        <Link className="enlace" to="/usuarios">Ver todos</Link>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {asignados === undefined ? (
        <p className="sutil">Cargando…</p>
      ) : asignados.length === 0 ? (
        <p className="vacio">
          Nadie tiene a {razonSocial} en su cartera: solo los administradores lo ven (RF-104).
        </p>
      ) : (
        <ul className="lista-compacta">
          {asignados.map((u) => (
            <li key={u.id}>
              <span className="colaborador">
                <span className="usuario-avatar diminuto" aria-hidden="true">{u.nombre.trim().charAt(0).toUpperCase()}</span>
                <span className="nombre" title={u.correo}>{u.nombre}</span>
                <span className="insignia rol">{u.rol}</span>
              </span>
              <button className="enlace" disabled={ocupado === u.id}
                onClick={() => void cambiarCartera(u.id, false)}>
                {ocupado === u.id ? 'Revocando…' : 'Revocar'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {disponibles.length > 0 && (
        <label className="asignar-colaborador">
          Asignar a alguien más
          <select className="compacta" value="" disabled={!!ocupado}
            onChange={(e) => void cambiarCartera(e.target.value, true)}>
            <option value="">— elegir colaborador —</option>
            {disponibles.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre} · {u.rol}</option>
            ))}
          </select>
        </label>
      )}

      <p className="nota">
        Fuera de su cartera, un colaborador no ve a este cliente ni en la lista ni por su
        enlace directo (cédula I-02, A-05). Asignar y revocar es cosa de administradores.
      </p>
    </div>
  )
}
